import { useState, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import styled from 'styled-components';

import {
  ModalPopup,
  Header,
  Text,
  TextAreaInput,
  Button,
  FlexRow,
} from '@summer/ui-components';

import { Box } from 'components/box/Box';
import { verifyBorrowerResidency } from 'state/server/borrowers';

const bypassReasonLengthError = 'Reason must be less than 256 characters';
const bypassReasonMissingError = 'Please enter a reason';

const VerifyBorrowerResidency = ({ isOpen, togglePopup, borrowerId }: any) => {
  const [bypassReason, setBypassReason] = useState('');
  const [clientError, setClientError] = useState<any>(null);

  const {
    mutate,
    isPending,
    error: serverError,
  } = useMutation({
    mutationFn: verifyBorrowerResidency,
    onSuccess: togglePopup,
    // eslint-disable-next-line no-console -- TODO: Determine logging method for admin-app.
    onError: console.error,
  });

  const validate = ({ bypassReason }: any) => {
    if (!bypassReason) {
      return bypassReasonMissingError;
    }

    if (bypassReason.length > 256) {
      return bypassReasonLengthError;
    }

    return null;
  };

  const handleSubmit = useCallback(
    async (e: any) => {
      e.preventDefault();
      const error = validate({ bypassReason });

      if (error) {
        setClientError(error);
        return;
      }

      mutate({ borrowerId, bypassReason });
    },
    [borrowerId, bypassReason, mutate],
  );

  const handleChange = useCallback((e: any) => {
    setClientError(null);
    setBypassReason(e.target.value);
  }, []);

  return (
    <ModalPopup
      togglePopup={togglePopup}
      isOpen={isOpen}
      width={770}
      component={() => (
        <Container>
          <Header h1>
            Are you sure you want to verify this borrower’s residency?
          </Header>
          <Text paragraph weight="medium">
            This borrower will bypass the residency verification step and can
            access Summer.
          </Text>
          {/* eslint-disable-next-line @typescript-eslint/no-misused-promises -- pre-existing; surfaced by reflow from a type-only change */}
          <form onSubmit={handleSubmit}>
            <TextAreaInput
              name="verifyBorrowerReason"
              label="Reason"
              placeholder="Lorem ipsum dolor sit."
              onChange={handleChange}
              value={bypassReason}
              error={clientError || (serverError && serverError.message)}
              touched={clientError || (serverError && serverError.message)}
            />
            <ButtonContainer>
              <Button secondary borderless onClick={togglePopup} width={120}>
                Cancel
              </Button>
              <Button
                type="submit"
                isLoading={isPending}
                disabled={clientError}
                width={180}
              >
                Verify Residency
              </Button>
            </ButtonContainer>
          </form>
        </Container>
      )}
    />
  );
};

const Container = styled(Box)`
  h1 {
    margin-bottom: 12px;
  }

  form {
    margin-top: 52px;
  }
`;

const ButtonContainer = styled(FlexRow)`
  justify-content: flex-end;
  gap: 16px;
`;

export default VerifyBorrowerResidency;
